'use client'

import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Dialog, Transition } from '@headlessui/react'
import {
  ChevronRight,
  Loader2,
  PanelRightClose,
  PanelRightOpen,
} from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'

import { useAuthInfo } from '@sweetauth/core'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

import { DEMO_URL } from '@/constants/general'

import { createLoginUrlWithRedirect } from '@/helpers/login'
import { useViewTransition } from '@/helpers/viewTransitions/useViewTransition'

import { useCanAccessPremium } from '@/hooks/billing/useCanAccessPremium'
import { useIsomorphicLayoutEffect } from '@/hooks/common/useIsomorphicLayoutEffect'
import { useRenderNowMs } from '@/hooks/common/useRenderNowMs'
import {
  useAggregateCashFlowCurves,
  useAwardTimeSeries,
  useIdvGroupTimeSeries,
  useRecipientAwardPortfolio,
  useRecipientCeilingTimeSeries,
  useRecipientOutlayTimeSeries,
} from '@/hooks/usaspending'

import { AwardExistenceLoaderProvider } from '@/services/award-existence-loader/context'

import type { ChartMode } from '../../charts/cashflow-utils'
import { formatCurrency, safeNum } from '../../charts/cashflow-utils'
import { EChartsCashFlow } from '../../charts/EChartsCashFlow'
import {
  type CashFlowSelection,
  type GroupedPortfolio,
  buildIdvChildColorMap,
  groupAwardPortfolio,
} from '../../charts/idv-grouping'
import { QueryErrorBoundary, isServiceWaking } from '../../DataErrorBoundary'
import { ChartModeToggle, SectionCard, SectionSkeleton } from '../../shared'
import { useDetailFilter } from '../../shared/DetailFilterContext'
import { type TimeRange, TimeRangeToggle } from '../../shared/TimeRangeToggle'
import { useChartExpanded } from '../ChartExpandedContext'
import { useRecipientDetail } from '../context'
import { CashFlowDetailBar, type DetailSelection } from './CashFlowDetailBar'
import { ShimmerBar } from './ShimmerBar'
import { type DrillLevel, useDrillDown } from './useDrillDown'
import { useExpandedChartOptions } from './useExpandedChartOptions'

// Lazy-load the sidebar: only fetched when first expanded
const AwardSidebar = React.lazy(() =>
  import('./AwardSidebar').then((m) => ({ default: m.AwardSidebar })),
)

/**
 * AwardCashFlowSection
 *
 * Unified cash flow chart that transitions between compact and expanded modes
 * using CSS transitions instead of swapping component trees. The ECharts
 * instance stays mounted throughout, and the sidebar slides in/out via
 * width transition.
 */
export function AwardCashFlowSection() {
  const { recipientId } = useRecipientDetail()
  const { isChartExpanded: isExpanded, setChartExpanded } = useChartExpanded()
  const [chartMode, setChartMode] = useState<ChartMode>('monthly')
  const [timeRange, setTimeRange] = useState<TimeRange>('ALL')

  const { startViewTransition, isTransitioning } = useViewTransition()
  const toggleExpanded = useCallback(() => {
    startViewTransition(() => setChartExpanded((prev) => !prev))
  }, [startViewTransition, setChartExpanded])

  return (
    <AwardExistenceLoaderProvider>
      <div
        id="award-cash-flow"
        style={{ viewTransitionName: 'cashflow-outer' }}
      >
        <div className="overflow-hidden rounded-xl border border-primary-200 bg-white shadow-sm hover:shadow-md dark:border-primary-700 dark:bg-primary-900">
          <QueryErrorBoundary title="Failed to load cash flow data">
            <Suspense fallback={<SectionSkeleton height={400} bars={0} />}>
              <CashFlowChart
                recipientId={recipientId}
                chartMode={chartMode}
                onChartModeChange={setChartMode}
                timeRange={timeRange}
                onTimeRangeChange={setTimeRange}
                isExpanded={isExpanded}
                onToggleExpanded={toggleExpanded}
                deferResize={isTransitioning}
              />
            </Suspense>
          </QueryErrorBoundary>
        </div>
      </div>
    </AwardExistenceLoaderProvider>
  )
}

// =============================================================================
// PremiumGateOverlay — sign-in / demo CTA shown over the chart
// =============================================================================

function PremiumGateOverlay({
  isLoggedIn,
  onDismiss,
}: {
  isLoggedIn: boolean
  onDismiss: () => void
}) {
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/80 dark:bg-zinc-900/80">
      <div className="mx-4 flex flex-col items-center rounded-xl border border-primary-200 bg-white px-6 py-5 shadow-lg sm:px-8 sm:py-6 dark:border-primary-700 dark:bg-zinc-800">
        <p className="text-center text-sm font-semibold text-primary-800 dark:text-primary-100">
          Sign in to explore individual awards
        </p>
        <p className="mt-1 text-center text-xs text-primary-500 dark:text-primary-400">
          Drill-down into specific contracts, grants, and IDVs
        </p>
        <div className="mt-4 flex items-center gap-3">
          {isLoggedIn ? (
            <Link
              href={DEMO_URL}
              target="_blank"
              className="rounded-md bg-accent-600 px-4 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-accent-700"
            >
              Schedule a Free Demo
            </Link>
          ) : (
            <a
              href={createLoginUrlWithRedirect()}
              className="rounded-md bg-accent-600 px-4 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-accent-700"
            >
              Sign in
            </a>
          )}
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs text-primary-400 hover:text-primary-600 dark:hover:text-primary-300"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// CashFlowSidebar — desktop (absolute) + mobile (drawer) sidebar
// =============================================================================

function CashFlowSidebar({
  showDesktop,
  mobileOpen,
  onMobileClose,
  grouped,
  selection,
  loadingSelection,
  referenceNowMs,
  onSelect,
  childColorMap,
}: {
  showDesktop: boolean
  mobileOpen: boolean
  onMobileClose: () => void
  grouped: GroupedPortfolio
  selection: CashFlowSelection
  loadingSelection?: CashFlowSelection
  referenceNowMs: number
  onSelect: (selection: CashFlowSelection) => boolean
  childColorMap?: Map<string, string>
}) {
  return (
    <>
      {/* Desktop sidebar — absolute so it spans the full card height */}
      {showDesktop && (
        <div
          className="absolute inset-y-0 right-0 hidden w-72 lg:w-80 xl:w-[340px] 2xl:w-96 md:flex"
          style={{ viewTransitionName: 'cashflow-sidebar' }}
        >
          <Suspense fallback={<div className="w-full" />}>
            <AwardSidebar
              grouped={grouped}
              selection={selection}
              loadingSelection={loadingSelection}
              referenceNowMs={referenceNowMs}
              onSelect={onSelect}
              childColorMap={childColorMap}
            />
          </Suspense>
        </div>
      )}

      {/* Mobile sidebar drawer */}
      <Transition.Root show={mobileOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-40 md:hidden"
          onClose={onMobileClose}
        >
          <Transition.Child
            as={React.Fragment}
            enter="transition-opacity duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-y-0 right-0 flex">
            <Transition.Child
              as={React.Fragment}
              enter="transform transition ease-out duration-200"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in duration-200"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="pointer-events-auto w-72 max-w-[85vw]">
                <Suspense
                  fallback={
                    <div className="h-full w-full bg-white dark:bg-primary-900" />
                  }
                >
                  <AwardSidebar
                    grouped={grouped}
                    selection={selection}
                    loadingSelection={loadingSelection}
                    referenceNowMs={referenceNowMs}
                    onSelect={onSelect}
                    childColorMap={childColorMap}
                    onClose={onMobileClose}
                  />
                </Suspense>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}

// =============================================================================
// DrillBreadcrumb (inline — only visible when expanded)
// =============================================================================

function DrillBreadcrumb({
  drillLevel,
  onNavigate,
}: {
  drillLevel: DrillLevel
  onNavigate: (level: DrillLevel) => void
}) {
  if (drillLevel.level === 0) return null

  const crumbs: { label: string; onClick?: () => void }[] = [
    { label: 'All Awards', onClick: () => onNavigate({ level: 0 }) },
  ]

  if (drillLevel.level === 1) {
    crumbs.push({ label: 'Categories' })
  }

  if (drillLevel.level === 2) {
    const sel = drillLevel.selection
    if (sel?.type === 'award') {
      crumbs.push({ label: sel.awardId })
    } else if (sel?.type === 'idv') {
      crumbs.push({ label: sel.parentPiid })
    }
  }

  if (drillLevel.level === 3) {
    crumbs.push({ label: drillLevel.parentPiid })
    crumbs.push({ label: 'Child Orders' })
  }

  return (
    <div className="flex min-w-0 items-center gap-1 overflow-hidden text-xs">
      {crumbs.map((crumb, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <ChevronRight className="h-3 w-3 flex-shrink-0 text-primary-300 dark:text-primary-600" />
          )}
          {crumb.onClick ? (
            <button
              type="button"
              onClick={crumb.onClick}
              className="flex-shrink-0 text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300"
            >
              {crumb.label}
            </button>
          ) : (
            <span
              className="truncate font-medium text-primary-600 max-w-[160px] sm:max-w-[240px] dark:text-primary-300"
              title={crumb.label}
            >
              {crumb.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

// =============================================================================
// ChartAreaContent — explicit variant component for chart area states
// =============================================================================

type ChartAreaState =
  | { status: 'ready' }
  | { status: 'no-data' }
  | { status: 'waking'; onRetry: () => void }

type ChartAreaContentProps = {
  state: ChartAreaState
} & Pick<
  React.ComponentProps<typeof EChartsCashFlow>,
  'option' | 'onClick' | 'resetKey' | 'deferResize' | 'zoomWindow'
>

const WAKING_RETRY_INTERVAL = 15

function WakingStateContent({ onRetry }: { onRetry: () => void }) {
  const [countdown, setCountdown] = useState(WAKING_RETRY_INTERVAL)
  const onRetryRef = useRef(onRetry)
  useEffect(() => {
    onRetryRef.current = onRetry
  })

  const triggerRetry = useCallback(() => {
    setCountdown(WAKING_RETRY_INTERVAL)
    onRetryRef.current()
  }, [])

  useEffect(() => {
    if (countdown <= 0) {
      triggerRetry()
      return
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000)
    return () => clearTimeout(t)
  }, [countdown, triggerRetry])

  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
      <Loader2 className="h-5 w-5 animate-spin" />
      <p className="text-sm">Award data is temporarily unavailable</p>
      <p className="text-xs text-slate-500">Analytics service is warming up…</p>
      <button
        type="button"
        onClick={triggerRetry}
        className="mt-1 text-xs text-accent-600 hover:underline"
      >
        Retrying in {countdown}s
      </button>
    </div>
  )
}

const ChartAreaContent = React.memo(function ChartAreaContent({
  state,
  option,
  onClick,
  resetKey,
  deferResize,
  zoomWindow,
}: ChartAreaContentProps) {
  if (state.status === 'waking') {
    return <WakingStateContent onRetry={state.onRetry} />
  }
  if (state.status === 'no-data') {
    return (
      <div className="flex h-full flex-col items-center justify-center text-slate-400">
        <p className="text-sm">No time series data available</p>
        <p className="mt-1 text-xs text-slate-500">See award details below</p>
      </div>
    )
  }
  return (
    <EChartsCashFlow
      option={option}
      onClick={onClick}
      resetKey={resetKey}
      deferResize={deferResize}
      zoomWindow={zoomWindow}
      showResetButton
    />
  )
})

// =============================================================================
// CashFlowChart — single unified chart component
// =============================================================================

function CashFlowChart({
  recipientId,
  chartMode,
  onChartModeChange,
  timeRange,
  onTimeRangeChange,
  isExpanded,
  onToggleExpanded,
  deferResize,
}: {
  recipientId: string
  chartMode: ChartMode
  onChartModeChange: (mode: ChartMode) => void
  timeRange: TimeRange
  onTimeRangeChange: (range: TimeRange) => void
  isExpanded: boolean
  onToggleExpanded: () => void
  deferResize?: React.RefObject<boolean>
}) {
  const { state: filterState } = useDetailFilter()
  const referenceNowMs = useRenderNowMs({
    live: isExpanded,
    refreshMs: isExpanded ? 60_000 : undefined,
  })

  // ─── Aggregate data (always active) ───────────────────────────────
  const {
    timeSeries,
    allAwards,
    excludedIdvTypes,
    portfolioData,
    outlayTimeSeries,
    obligationCurve,
    ceilingCurve,
    ceilingPreRange,
    outlayRatio,
    preReportingOutlay,
    outlayCurve,
    hasPreReportingData,
    estimatedOutlayPct,
    isFetching: isAggregateFetching,
  } = useAggregateCashFlowCurves(recipientId)

  // Loading labels for shimmer (track individual fetches)
  const { isFetching: outlayFetching } =
    useRecipientOutlayTimeSeries(recipientId)
  const { isFetching: ceilingFetching } =
    useRecipientCeilingTimeSeries(recipientId)
  const { isFetching: portfolioFetching } =
    useRecipientAwardPortfolio(recipientId)
  const isFetchingCashFlow =
    outlayFetching || ceilingFetching || portfolioFetching

  const fetchingLabel = useMemo(() => {
    if (!isFetchingCashFlow) return null
    const parts: string[] = []
    if (ceilingFetching) parts.push('ceiling')
    if (outlayFetching) parts.push('disbursements')
    if (portfolioFetching) parts.push('portfolio')
    return `Loading ${parts.join(' & ')}…`
  }, [ceilingFetching, isFetchingCashFlow, outlayFetching, portfolioFetching])

  // ─── Auth gate (always called) ────────────────────────────────────
  const { isLoggedIn } = useAuthInfo()
  const { canAccessPremium } = useCanAccessPremium()

  // ─── Drill-down state (always called; no-op when compact) ─────────
  const {
    selection,
    setSelection,
    drillLevel,
    setDrillLevel,
    effectiveMode,
    showGateOverlay,
    handleModeToggle,
    handleSelect: drillSelect,
    handleDrillNavigate,
    handleRequestGate,
    handleDismissGate,
    handleCategoryDrill,
  } = useDrillDown(
    recipientId,
    chartMode,
    onChartModeChange,
    canAccessPremium ?? false,
  )

  // ─── Sidebar visibility ───────────────────────────────────────────
  const [showDesktopSidebar, setShowDesktopSidebar] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const closeMobileSidebar = useCallback(() => setMobileSidebarOpen(false), [])

  // On expand, resolve sidebar visibility before paint
  useIsomorphicLayoutEffect(() => {
    if (!isExpanded) {
      setShowDesktopSidebar(false)
      setMobileSidebarOpen(false)
      return
    }
    const mql = window.matchMedia('(min-width: 768px)')
    setShowDesktopSidebar(mql.matches)
    if (!mql.matches) {
      setMobileSidebarOpen(true)
    }
    const handler = (e: MediaQueryListEvent) => setShowDesktopSidebar(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [isExpanded])

  // ─── Grouped portfolio (always computed — pure, fast) ─────────────
  const grouped = useMemo(
    () => groupAwardPortfolio(allAwards ?? []),
    [allAwards],
  )

  // ─── Per-award time series (no-op when null) ──────────────────────
  const selectedAwardId = selection?.type === 'award' ? selection.awardId : null
  const {
    data: awardTimeSeries,
    isLoading: isAwardTimeSeriesLoading,
    isError: isAwardTimeSeriesError,
    error: awardTimeSeriesError,
    refetch: refetchAwardTimeSeries,
  } = useAwardTimeSeries(selectedAwardId)

  const selectedAward = useMemo(() => {
    if (selection?.type !== 'award' || !allAwards) return null
    return (
      allAwards.find(
        (a) => a.generated_unique_award_id === selection.awardId,
      ) ?? null
    )
  }, [selection, allAwards])

  const selectedIdvGroup = useMemo(() => {
    if (selection?.type !== 'idv') return null
    return (
      grouped.idvGroups.find((g) => g.parentPiid === selection.parentPiid) ??
      null
    )
  }, [selection, grouped])

  // Single-child IDV monthly fallback
  const singleIdvChildAwardId = useMemo(() => {
    if (selection?.type !== 'idv' || !selectedIdvGroup) return null
    return selectedIdvGroup.children.length === 1
      ? selectedIdvGroup.children[0].generated_unique_award_id
      : null
  }, [selection, selectedIdvGroup])
  const { data: singleChildTimeSeries, isLoading: isSingleChildTsLoading } =
    useAwardTimeSeries(singleIdvChildAwardId)

  // IDV group time series — keep alive when child TO selected
  const selectedParentPiid = useMemo(() => {
    if (selection?.type === 'idv') return selection.parentPiid
    if (selection?.type === 'award') {
      for (const g of grouped.idvGroups) {
        if (
          g.children.some(
            (c) => c.generated_unique_award_id === selection.awardId,
          )
        ) {
          return g.parentPiid
        }
      }
    }
    return null
  }, [selection, grouped])
  const {
    data: idvGroupTimeSeriesData,
    isLoading: isIdvGroupTimeSeriesLoading,
    isError: isIdvGroupTimeSeriesError,
    error: idvGroupTimeSeriesError,
    refetch: refetchIdvGroupTimeSeries,
  } = useIdvGroupTimeSeries(recipientId, selectedParentPiid)

  const isChartLoading =
    (selection?.type === 'award' && isAwardTimeSeriesLoading) ||
    (selection?.type === 'idv' && isIdvGroupTimeSeriesLoading) ||
    (selection?.type === 'idv' &&
      effectiveMode === 'monthly' &&
      singleIdvChildAwardId != null &&
      isSingleChildTsLoading)

  const isAwardTimeSeriesWaking =
    isAwardTimeSeriesError && isServiceWaking(awardTimeSeriesError)
  const isIdvGroupTimeSeriesWaking =
    isIdvGroupTimeSeriesError && isServiceWaking(idvGroupTimeSeriesError)
  const isChartServiceWaking =
    isAwardTimeSeriesWaking || isIdvGroupTimeSeriesWaking

  // Use refs so the retry callback stays stable without re-creating on every
  // error-state change (rerender-use-ref-transient-values).
  const isAwardTimeSeriesWakingRef = useRef(isAwardTimeSeriesWaking)
  isAwardTimeSeriesWakingRef.current = isAwardTimeSeriesWaking
  const isIdvGroupTimeSeriesWakingRef = useRef(isIdvGroupTimeSeriesWaking)
  isIdvGroupTimeSeriesWakingRef.current = isIdvGroupTimeSeriesWaking

  const handleRetryChartTimeSeries = useCallback(() => {
    if (isAwardTimeSeriesWakingRef.current) void refetchAwardTimeSeries()
    if (isIdvGroupTimeSeriesWakingRef.current) void refetchIdvGroupTimeSeries()
  }, [refetchAwardTimeSeries, refetchIdvGroupTimeSeries])

  // IDV child series ID map (for chart click → award navigation)
  const idvChildSeriesIdMap = useMemo(() => {
    const map = new Map<string, string>()
    if (!idvGroupTimeSeriesData || !selectedIdvGroup) return map
    idvGroupTimeSeriesData.children.forEach((child, i) => {
      const groupChild = selectedIdvGroup.children.find(
        (c) => c.generated_unique_award_id === child.awardId,
      )
      const label = groupChild?.display_award_id ?? `TO ${i + 1}`
      map.set(label, child.awardId)
    })
    return map
  }, [idvGroupTimeSeriesData, selectedIdvGroup])

  // IDV child color map
  const colorMapIdvGroup = useMemo(() => {
    if (!selectedParentPiid) return null
    return (
      grouped.idvGroups.find((g) => g.parentPiid === selectedParentPiid) ?? null
    )
  }, [selectedParentPiid, grouped])

  const idvChildColorMap = useMemo(
    () =>
      buildIdvChildColorMap(idvGroupTimeSeriesData ?? null, colorMapIdvGroup),
    [idvGroupTimeSeriesData, colorMapIdvGroup],
  )

  // ─── Chart options (single path for both modes) ───────────────────
  // When compact: drillLevel=0, selection=null, expanded=false → aggregate option
  // When expanded: full drill-down support with expanded=true
  const { displayChartOption, zoomWindow, noTimeSeriesData } =
    useExpandedChartOptions({
      expanded: isExpanded,
      effectiveMode: isExpanded ? effectiveMode : chartMode,
      drillLevel: isExpanded ? drillLevel : { level: 0 },
      selection: isExpanded ? selection : null,
      selectedAward: isExpanded ? selectedAward : null,
      awardTimeSeries: isExpanded ? awardTimeSeries : undefined,
      isAwardTimeSeriesLoading: isExpanded ? isAwardTimeSeriesLoading : false,
      selectedIdvGroup: isExpanded ? selectedIdvGroup : null,
      idvGroupTimeSeriesData: isExpanded ? idvGroupTimeSeriesData : undefined,
      isIdvGroupTimeSeriesLoading: isExpanded
        ? isIdvGroupTimeSeriesLoading
        : false,
      singleChildTimeSeries: isExpanded ? singleChildTimeSeries : undefined,
      isChartLoading: isExpanded ? isChartLoading : false,
      timeSeries,
      obligationCurve,
      ceilingCurve,
      ceilingPreRange,
      outlayTimeSeries,
      outlayRatio,
      preReportingOutlay,
      outlayCurve,
      hasPreReportingData,
      estimatedOutlayPct,
      fiscalYear: filterState.fiscalYear,
      timeRange,
      grouped,
    })

  // ─── Chart click handler ──────────────────────────────────────────
  // Stored in a ref so the stable `handleChartClick` callback never
  // changes — keeping React.memo on EChartsCashFlow effective.
  const router = useRouter()

  const chartClickRef = useRef<(params: { seriesName?: string }) => void>(
    () => {},
  )
  chartClickRef.current = (params: { seriesName?: string }) => {
    if (!isExpanded) return
    if (!canAccessPremium) {
      handleRequestGate()
      return
    }
    const sn = params.seriesName

    if (
      drillLevel.level === 2 &&
      selection?.type === 'award' &&
      selectedAward
    ) {
      if (sn === 'Activity') {
        router.push(
          `/awards/usaspending/${selectedAward.generated_unique_award_id}`,
        )
        return
      }
    }

    if (drillLevel.level === 2 && selection?.type === 'idv' && sn) {
      const awardId = idvChildSeriesIdMap.get(sn)
      if (awardId) {
        router.push(`/awards/usaspending/${awardId}`)
        return
      }
    }

    if (drillLevel.level === 1 && sn) {
      if (sn === 'Contracts' && grouped.standaloneContracts[0]) {
        const award = grouped.standaloneContracts[0]
        const sel: CashFlowSelection = {
          type: 'award',
          awardId: award.generated_unique_award_id,
        }
        setSelection(sel)
        setDrillLevel({ level: 2, selection: sel })
      } else if (sn === 'Grants' && grouped.grants[0]) {
        const award = grouped.grants[0]
        const sel: CashFlowSelection = {
          type: 'award',
          awardId: award.generated_unique_award_id,
        }
        setSelection(sel)
        setDrillLevel({ level: 2, selection: sel })
      } else if (sn === 'IDVs' && grouped.idvGroups[0]) {
        const group = grouped.idvGroups[0]
        const sel: CashFlowSelection = {
          type: 'idv',
          parentPiid: group.parentPiid,
        }
        setSelection(sel)
        setDrillLevel({ level: 2, selection: sel })
      }
    }
  }

  const handleChartClick = useCallback(
    (params: { seriesName?: string }) => chartClickRef.current(params),
    [],
  )

  // ─── Detail bar selection ─────────────────────────────────────────
  const aggregateTotalOutlay =
    portfolioData?.total_outlays ?? grouped.totalOutlay

  const detailSelection = useMemo((): DetailSelection => {
    if (selection?.type === 'award' && selectedAward) {
      return { type: 'award', award: selectedAward }
    }
    if (selection?.type === 'idv' && selectedIdvGroup) {
      return { type: 'idv', group: selectedIdvGroup }
    }
    return {
      type: 'aggregate',
      totalCeiling: portfolioData?.total_ceiling ?? grouped.totalCeiling,
      totalObligation:
        portfolioData?.total_obligation ?? grouped.totalObligation,
      totalOutlay: aggregateTotalOutlay,
      awardCount: portfolioData?.total_count ?? grouped.awardCount,
    }
  }, [
    selection,
    selectedAward,
    selectedIdvGroup,
    grouped,
    portfolioData,
    aggregateTotalOutlay,
  ])

  // ─── Compact stats (only used when collapsed) ─────────────────────
  const compactStats = useMemo(() => {
    const totalObligated =
      portfolioData?.total_obligation ??
      (obligationCurve.length > 0
        ? obligationCurve[obligationCurve.length - 1].y
        : 0)
    const totalCeiling = portfolioData
      ? portfolioData.total_ceiling
      : ceilingCurve && ceilingCurve.length > 0
        ? ceilingCurve[ceilingCurve.length - 1].y
        : null
    const modeledOutlayTotal =
      outlayCurve && outlayCurve.length > 0
        ? outlayCurve[outlayCurve.length - 1].y
        : null
    const totalDisbursed =
      hasPreReportingData && modeledOutlayTotal != null
        ? modeledOutlayTotal
        : portfolioData
          ? portfolioData.total_outlays
          : modeledOutlayTotal

    const safeCeiling =
      totalCeiling != null && Number.isFinite(totalCeiling)
        ? totalCeiling
        : null
    const safeObl = Number.isFinite(totalObligated) ? totalObligated : 0
    const safeDisbursed =
      totalDisbursed != null && Number.isFinite(totalDisbursed)
        ? totalDisbursed
        : null

    const ceilingPct =
      safeCeiling && safeCeiling > 0
        ? ((safeObl / safeCeiling) * 100).toFixed(1)
        : null
    const disbursedPct =
      safeObl > 0 && safeDisbursed != null
        ? ((safeDisbursed / safeObl) * 100).toFixed(1)
        : null

    return {
      totalObligated: safeObl,
      totalCeiling: safeCeiling,
      totalDisbursed: safeDisbursed,
      ceilingPct,
      disbursedPct,
    }
  }, [
    obligationCurve,
    ceilingCurve,
    outlayCurve,
    hasPreReportingData,
    portfolioData,
  ])

  const showLoading = isExpanded
    ? isAggregateFetching || isChartLoading
    : isFetchingCashFlow

  const loadingLabel = isExpanded
    ? isChartLoading
      ? 'Loading award…'
      : 'Loading…'
    : fetchingLabel

  const chartOption = displayChartOption

  // Stable state object so React.memo on ChartAreaContent can bail out
  // when the chart area state hasn't actually changed.
  const chartAreaState = useMemo((): ChartAreaState => {
    if (noTimeSeriesData && isExpanded) {
      if (isChartServiceWaking)
        return { status: 'waking', onRetry: handleRetryChartTimeSeries }
      return { status: 'no-data' }
    }
    return { status: 'ready' }
  }, [
    noTimeSeriesData,
    isExpanded,
    isChartServiceWaking,
    handleRetryChartTimeSeries,
  ])

  // ─── Render ───────────────────────────────────────────────────────

  return (
    <div className="relative">
      {/* Content column — margin-right reserves space for the sidebar */}
      <div
        className={`flex flex-col ${isExpanded && showDesktopSidebar ? 'md:mr-72 lg:mr-80 xl:mr-[340px] 2xl:mr-96' : ''}`}
      >
        {/* Header bar */}
        <div className="flex items-center justify-between border-b border-primary-100 px-3 py-2 sm:px-4 sm:py-2.5 dark:border-primary-800">
          <div className="min-w-0 flex-1">
            <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary-900 dark:text-primary-100">
              Award Cash Flow
              {showLoading && loadingLabel && (
                <span className="flex items-center gap-1.5 text-xs font-normal normal-case tracking-normal text-primary-400 dark:text-primary-500">
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                  {loadingLabel}
                </span>
              )}
            </h3>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden sm:block">
              <TimeRangeToggle value={timeRange} onChange={onTimeRangeChange} />
            </div>
            <ChartModeToggle
              mode={isExpanded ? effectiveMode : chartMode}
              onChange={isExpanded ? handleModeToggle : onChartModeChange}
            />
            {isExpanded && (
              <button
                type="button"
                onClick={() => setMobileSidebarOpen(true)}
                className="-m-2 p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 md:hidden"
                aria-label="Show award portfolio"
                title="Award portfolio"
              >
                <PanelRightOpen className="h-4 w-4" />
              </button>
            )}
            {isExpanded ? (
              <button
                type="button"
                onClick={onToggleExpanded}
                className="-m-2 hidden items-center gap-1 p-2 text-xs font-medium text-blue-600 hover:text-blue-800 md:flex dark:text-blue-400 dark:hover:text-blue-300"
                aria-label="Collapse explorer"
                title="Collapse"
              >
                <PanelRightClose className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={onToggleExpanded}
                className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                title="Explore Awards"
              >
                <span className="hidden sm:inline">Explore Awards</span>
                <PanelRightOpen className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Breadcrumb row — fixed height to prevent layout shift, hidden when compact */}
        <div
          className={`overflow-hidden ${
            isExpanded ? 'h-7 opacity-100' : 'h-0 opacity-0'
          }`}
          style={
            isExpanded
              ? { viewTransitionName: 'cashflow-breadcrumb' }
              : undefined
          }
        >
          <div className="flex h-7 items-center gap-2 px-3 sm:px-4">
            <DrillBreadcrumb
              drillLevel={drillLevel}
              onNavigate={handleDrillNavigate}
            />
            {drillLevel.level === 0 && isExpanded && (
              <button
                type="button"
                onClick={handleCategoryDrill}
                className="text-xs font-medium text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300"
              >
                Breakdown by Category →
              </button>
            )}
          </div>
        </div>

        {/* Shimmer bar — always rendered to hold its 2px height */}
        <ShimmerBar visible={showLoading} />

        {/* Chart area */}
        <div
          className={`relative ${
            isExpanded
              ? 'h-[60vh] max-h-[720px] min-h-[280px] sm:min-h-[400px]'
              : 'h-[400px]'
          }`}
        >
          <ChartAreaContent
            state={chartAreaState}
            option={chartOption}
            onClick={isExpanded ? handleChartClick : undefined}
            resetKey={
              !isExpanded || selection === null
                ? 'aggregate'
                : selection.type === 'award'
                  ? selection.awardId
                  : selection.parentPiid
            }
            deferResize={deferResize}
            zoomWindow={zoomWindow}
          />

          {/* Grey overlay while fetching per-selection time series */}
          {isExpanded && isChartLoading && (
            <div className="absolute inset-0 bg-white/60 dark:bg-zinc-900/60" />
          )}

          {/* Premium gate overlay */}
          {isExpanded && showGateOverlay && (
            <PremiumGateOverlay
              isLoggedIn={isLoggedIn ?? false}
              onDismiss={handleDismissGate}
            />
          )}
        </div>

        {/* Stats bar — compact stats when collapsed, detail bar when expanded */}
        {isExpanded ? (
          <CashFlowDetailBar
            selection={detailSelection}
            referenceNowMs={referenceNowMs}
            hasPreReportingData={hasPreReportingData}
            excludedIdvTypes={excludedIdvTypes}
          />
        ) : (
          <CashFlowStatsBar
            stats={compactStats}
            hasPreReportingData={hasPreReportingData}
            excludedIdvTypes={excludedIdvTypes}
          />
        )}
      </div>

      {isExpanded && (
        <CashFlowSidebar
          showDesktop={showDesktopSidebar}
          mobileOpen={mobileSidebarOpen}
          onMobileClose={closeMobileSidebar}
          grouped={grouped}
          selection={selection}
          loadingSelection={isChartLoading ? selection : undefined}
          referenceNowMs={referenceNowMs ?? Date.now()}
          onSelect={drillSelect}
          childColorMap={idvChildColorMap}
        />
      )}
    </div>
  )
}

// =============================================================================
// CashFlowStatsBar — compact stats strip
// =============================================================================

interface CashFlowStats {
  totalCeiling: number | null
  totalObligated: number
  totalDisbursed: number | null
  ceilingPct: string | null
  disbursedPct: string | null
}

function CashFlowStatsBar({
  stats,
  hasPreReportingData,
  excludedIdvTypes,
}: {
  stats: CashFlowStats
  hasPreReportingData?: boolean
  excludedIdvTypes?: string[]
}) {
  const {
    totalCeiling,
    totalObligated,
    totalDisbursed,
    ceilingPct,
    disbursedPct,
  } = stats
  const sep = <span className="text-primary-200 dark:text-primary-700">·</span>
  return (
    <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 border-t border-primary-100 px-3 py-1.5 text-xs dark:border-primary-800 sm:px-4">
      {totalCeiling != null && (
        <>
          <span title="Sum of award ceilings (base + all options value)">
            <span className="text-primary-400 dark:text-primary-500">
              Ceiling{' '}
            </span>
            <span className="font-semibold text-primary-700 dark:text-primary-200">
              {formatCurrency(totalCeiling)}
            </span>
          </span>
          {sep}
        </>
      )}
      <span>
        <span className="text-primary-400 dark:text-primary-500">
          Obligated{' '}
        </span>
        <span className="font-semibold text-blue-600 dark:text-blue-400">
          {formatCurrency(totalObligated)}
        </span>
        {ceilingPct && (
          <span className="text-primary-400 dark:text-primary-500">
            {' '}
            ({ceilingPct}%)
          </span>
        )}
      </span>
      {totalDisbursed != null && (
        <>
          {sep}
          <span>
            <span className="text-primary-400 dark:text-primary-500">
              Disbursed{' '}
            </span>
            <span className="font-semibold text-green-600 dark:text-green-400">
              {formatCurrency(totalDisbursed)}
            </span>
            {disbursedPct && (
              <span className="text-primary-400 dark:text-primary-500">
                {' '}
                ({disbursedPct}%)
              </span>
            )}
          </span>
        </>
      )}
      {hasPreReportingData && (
        <>
          {sep}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help text-amber-600/70 underline decoration-dotted decoration-amber-400/40 dark:text-amber-400/70 dark:decoration-amber-300/30">
                Pre-FY2022 disbursement estimated
              </span>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              inverseColors
              className="max-w-xs space-y-1.5 px-3 py-2 text-xs"
            >
              <p>
                Prior to FY2022, award-level outlay (disbursement) data was not
                required in monthly DATA Act submissions. Pre-FY2022 figures
                shown here are estimated from obligation data and the
                agency&apos;s outlay-to-obligation ratio.
              </p>
              <p>
                Beginning in FY2022, DAIMS v2.1 required all agencies to report
                File C award outlays monthly.{' '}
                <a
                  href="https://wwwkc.fiscal.treasury.gov/files/data-transparency/daims-release-notes-v2.1.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  DAIMS v2.1 Release Notes →
                </a>
              </p>
            </TooltipContent>
          </Tooltip>
        </>
      )}
      {excludedIdvTypes && excludedIdvTypes.length > 0 && (
        <>
          {sep}
          <span className="text-primary-300 dark:text-primary-600">
            {excludedIdvTypes.join(' & ')} vehicles excluded
          </span>
        </>
      )}
    </div>
  )
}
